import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogProps,
  DialogTitle,
  IconButton
} from "@mui/material";
import { FileUpload } from "../../components";
import { GeoJsonObject } from "geojson";
import { useState } from "react";
import { Accept } from "react-dropzone/.";
import { parseDbf, parseShp, parseZip, combine } from "./readSHP";
import { FeatureCollectionWithFilename } from "./readSHP/types";

export type GeometryImportTypes = "zip" | "shp" | "json" | undefined;
export type GeometryParser = (files: File[]) => Promise<(GeoJsonObject | GeoJsonObject[])[]>;

export interface GeometryImportProps extends Omit<Omit<DialogProps, 'children'>, 'fullScreen'> {
  type?: GeometryImportTypes;
  geometryParser?: GeometryParser;
  onFinish?: (geometry: (GeoJsonObject | GeoJsonObject[])[]) => void;
}

export const ImportGeometry: React.FC<GeometryImportProps> = ({ type, onFinish, geometryParser, ...modalProps }) => {
  const tipoGeom = type ?? "json";

  const [geometry, setGeometry] = useState<(GeoJsonObject | GeoJsonObject[])[] | undefined>(undefined);
  const [files, setFiles] = useState<File[] | undefined>(undefined);

  const handleClose = () => {
    setFiles(undefined);
    setGeometry(undefined);

    if (modalProps.onClose) {
      modalProps.onClose({}, 'escapeKeyDown');
    }
  };

  const handleFinish = () => {
    if (!geometry) return;
    if (onFinish) onFinish(geometry);
    setFiles(undefined);
    setGeometry(undefined);
  };

  const handleAddFile = async (files?: File[]) => {
    setFiles(files);
    if (!files) return;

    let func: GeometryParser = processShapefile;
    if (geometryParser) {
      func = geometryParser;
    }

    const geom = await func(files);
    if (!geom) return;
    setGeometry(geom);
  };

  let accept: Accept = {};
  let typeText = "Arquivo";
  if (tipoGeom === "shp") {
    typeText = "Shapefile";
    accept = { 'application/octet-stream': ['.shp', '.shx', '.dbf', '.prj'] };
  } else if (tipoGeom === "zip") {
    typeText = "ZIP";
    accept = { 'application/zip': ['.zip'] };
  } else if (tipoGeom === "json") {
    typeText = "GeoJSON ou JSON";
    accept = { 'application/json': ['.json', ".geojson"] };
  }

  return <Dialog {...modalProps}>
    <DialogTitle
      sx={{ m: 0, p: 2 }}
      id="customized-dialog-title"
    >
      {`Importar ${typeText}`}
    </DialogTitle>
    <IconButton
      aria-label="close"
      onClick={handleClose}
      sx={(theme) => ({
        position: 'absolute',
        right: 8,
        top: 8,
        color: theme.palette.grey[500],
      })}
    >
      <Close />
    </IconButton>
    <FileUpload
      onAddFile={handleAddFile}
      files={files}
      sx={{
        width: "350px",
        height: "350px",
        padding: "15px",
      }}
      accept={accept}
      multiple={true}
    />
    {geometry && <DialogActions>
      <Button autoFocus onClick={handleFinish}>
        Finalizar
      </Button>
    </DialogActions>}
  </Dialog>;
};

const processShapefile = async (files: File[]) => {
  const geojson: (FeatureCollectionWithFilename | FeatureCollectionWithFilename[])[] = [];

  for (const file of files) {
    if (file.name.endsWith(".json") || file.name.endsWith(".geojson")) {
      geojson.push(JSON.parse(await file.text()));
    } else if (file.name.endsWith(".zip")) {
      const data = await parseZip(await file.arrayBuffer());
      data.fileName = file.name.replaceAll(".zip", "");
      geojson.push(await parseZip(await file.arrayBuffer()));
    } else if (file.name.endsWith(".shp")) {
      const nameFile = file.name.split(".")[0];
      const shpBuffer = await file.arrayBuffer();

      const object: {
        shp: ArrayBuffer;
        dbf?: ArrayBuffer;
        prj?: string;
        cpg?: ArrayBuffer;
      } = { shp: shpBuffer };

      for (const file of files) {
        if (!file.name.startsWith(nameFile)) continue;

        if (file.name.endsWith('.dbf')) {
          object.dbf = await file.arrayBuffer();
          continue;
        }
        if (file.name.endsWith('.prj')) {
          object.prj = await file.text();
          continue;
        }
        if (file.name.endsWith('.cpg')) {
          object.cpg = await file.arrayBuffer();
          continue;
        }
      }
      const combineList: [GeoJSON.Geometry[], GeoJSON.GeoJsonProperties[] | undefined] = [parseShp(object.shp, object.prj) as GeoJSON.Geometry[], undefined];
      if (object.dbf) {
        combineList[1] = parseDbf(object.dbf, object.cpg);
        geojson.push(combine(combineList as [GeoJSON.Geometry[], GeoJSON.GeoJsonProperties[]]) as (FeatureCollectionWithFilename | FeatureCollectionWithFilename[]));
      } else {
        const geom: FeatureCollectionWithFilename = {
          type: "FeatureCollection",
          fileName: nameFile,
          features: combineList[0].map((geom, i) => {
            return {
              type: "Feature",
              geometry: geom,
              id: i,
              properties: {},
            };
          }),
        };
        geojson.push(geom);
      }
    }
  }
  return geojson;
};

